import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, UserPlus, Pencil, UserX, UserCheck, Trash2, Users, ShieldAlert, Mail, Phone, Briefcase, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
    { value: 'pilote_projet', label: 'Pilote projet' },
    { value: 'directeur_projet', label: 'Directeur projet' },
    { value: 'direction_generale', label: 'Direction générale' },
    { value: 'daf', label: 'DAF' },
    { value: 'controle_gestion', label: 'Contrôle de gestion' },
    { value: 'comptable', label: 'Comptable' },
    { value: 'auditeur', label: 'Auditeur' },
    { value: 'financeur', label: 'Financeur' },
    { value: 'responsable_ong', label: 'Responsable ONG' },
    { value: 'gestionnaire_subventions', label: 'Gestionnaire subventions' },
    { value: 'coordinateur_terrain', label: 'Coordinateur terrain' },
    { value: 'consultant_externe', label: 'Consultant externe' },
];

export default function Team() {
    const { session, profile } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const isAdmin = currentWorkspace?.is_admin || profile?.is_super_admin;
    const [items, setItems] = useState([]);
    const [quota, setQuota] = useState({ used: 0, max: 0 });
    const [loading, setLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [tempPassword, setTempPassword] = useState(null);

    const auth = { headers: { Authorization: `Bearer ${session?.access_token}` } };

    const load = async () => {
        if (!currentWorkspace?.id) return;
        setLoading(true);
        try {
            const r = await axios.get(`${API}/team?workspace_id=${currentWorkspace.id}`, auth);
            setItems(r.data.items || []);
            setQuota(r.data.quota || { used: 0, max: 0 });
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
        setLoading(false);
    };

    useEffect(() => { if (session && currentWorkspace) load(); /* eslint-disable-next-line */ }, [session, currentWorkspace?.id]);

    const submitInvite = async (e) => {
        e.preventDefault();
        const f = e.currentTarget;
        const body = {
            workspace_id: currentWorkspace.id,
            email: f.email.value.trim(),
            first_name: f.first_name.value.trim(),
            last_name: f.last_name.value.trim(),
            phone: f.phone.value.trim() || null,
            job_function: f.job_function.value.trim() || null,
            role: f.role.value,
            is_admin: f.is_admin.checked,
            temp_password: f.temp_password.value.trim() || null,
        };
        try {
            const r = await axios.post(`${API}/team/invite`, body, auth);
            setTempPassword({ email: body.email, password: r.data.temp_password, applied: r.data.password_applied !== false });
            setInviteOpen(false);
            toast.success('Utilisateur invité avec succès');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const submitEdit = async (e) => {
        e.preventDefault();
        const f = e.currentTarget;
        const [first_name, ...rest] = (editing.full_name || '').split(' ');
        const last_name = rest.join(' ');
        const body = {
            workspace_id: currentWorkspace.id,
            first_name: f.first_name.value.trim() || first_name,
            last_name: f.last_name.value.trim() || last_name,
            phone: f.phone.value.trim() || null,
            job_function: f.job_function.value.trim() || null,
            role: f.role.value,
            is_admin: f.is_admin.checked,
        };
        try {
            await axios.patch(`${API}/team/${editing.user_id}`, body, auth);
            setEditing(null);
            toast.success('Modifications enregistrées');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const toggleDisabled = async (m) => {
        const next = !m.disabled_at;
        try {
            await axios.post(`${API}/team/${m.user_id}/toggle-disabled`,
                { workspace_id: currentWorkspace.id, disabled: next }, auth);
            toast.success(next ? 'Utilisateur désactivé' : 'Utilisateur réactivé');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const deleteMember = async (m) => {
        try {
            await axios.delete(`${API}/team/${m.user_id}?workspace_id=${currentWorkspace.id}`, auth);
            setConfirmDelete(null);
            toast.success('Utilisateur retiré du workspace');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    if (!currentWorkspace) {
        return <div className="p-8 text-foreground/65">Sélectionnez un workspace.</div>;
    }

    if (!isAdmin && !profile?.is_super_admin) {
        return (
            <div className="p-8 max-w-xl mx-auto" data-testid="team-no-access">
                <div className="card-soft p-8 text-center">
                    <ShieldAlert size={28} className="mx-auto text-destructive mb-3" />
                    <h2 className="font-heading text-xl mb-2">Accès réservé aux administrateurs</h2>
                    <p className="text-foreground/65">Seuls les admins de l'espace peuvent gérer les utilisateurs.</p>
                </div>
            </div>
        );
    }

    const pct = quota.max > 0 ? Math.min(100, (quota.used / quota.max) * 100) : 0;
    const quotaReached = quota.used >= quota.max;

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-8" data-testid="page-team">
            {/* Header */}
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="eyebrow text-primary mb-2">Équipe</div>
                    <h1 className="font-heading text-3xl">Gérer les utilisateurs</h1>
                    <p className="text-foreground/65 mt-2 max-w-xl text-[15px] leading-[1.6]">
                        Invitez vos collaborateurs, attribuez les rôles, désactivez ou retirez les accès.
                        Tout est journalisé dans les audit logs.
                    </p>
                </div>
                <Button
                    onClick={() => setInviteOpen(true)}
                    disabled={quotaReached}
                    className="bg-primary hover:bg-primary-hover"
                    data-testid="team-invite-btn"
                >
                    <UserPlus size={14} className="mr-2" /> Inviter un utilisateur
                </Button>
            </div>

            {/* Quota bar */}
            <div className="card-soft p-5" data-testid="team-quota">
                <div className="flex items-center justify-between text-sm mb-2">
                    <div className="flex items-center gap-2">
                        <Users size={14} className="text-primary" />
                        <span className="text-foreground/85">Utilisateurs actifs</span>
                    </div>
                    <span className="font-mono text-foreground">{quota.used} / {quota.max >= 9999 ? '∞' : quota.max}</span>
                </div>
                <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
                    <div className={`h-full transition-all ${pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-amber-500' : 'bg-secondary'}`}
                        style={{ width: `${pct}%` }} />
                </div>
                {quotaReached && (
                    <p className="text-xs text-amber-700 mt-3 flex items-center gap-1.5">
                        <Sparkles size={11} /> Quota atteint — passez à un plan supérieur ou ajoutez l'option +5 utilisateurs.
                    </p>
                )}
            </div>

            {/* Members table */}
            <div className="card-soft overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-foreground/60"><Loader2 className="mx-auto animate-spin" /></div>
                ) : items.length === 0 ? (
                    <div className="p-12 text-center text-foreground/60">Aucun utilisateur — invitez-en un.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-surface-muted/40">
                            <tr className="text-left text-foreground/65 text-xs uppercase tracking-wider">
                                <th className="px-5 py-3.5">Utilisateur</th>
                                <th className="px-5 py-3.5">Contact</th>
                                <th className="px-5 py-3.5">Fonction</th>
                                <th className="px-5 py-3.5">Rôle</th>
                                <th className="px-5 py-3.5">Statut</th>
                                <th className="px-5 py-3.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((m) => (
                                <tr key={m.user_id} className="border-t border-border hover:bg-surface-muted/30" data-testid={`team-row-${m.user_id}`}>
                                    <td className="px-5 py-4">
                                        <div className="font-medium text-foreground">{m.full_name || '—'}</div>
                                        <div className="text-xs text-foreground/55">{m.email}</div>
                                    </td>
                                    <td className="px-5 py-4 text-foreground/70 text-xs">
                                        {m.phone && <div className="flex items-center gap-1"><Phone size={11} /> {m.phone}</div>}
                                    </td>
                                    <td className="px-5 py-4 text-foreground/75">{m.job_function || '—'}</td>
                                    <td className="px-5 py-4">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-foreground/85">{ROLES.find(r => r.value === m.role)?.label || m.role}</span>
                                            {m.is_admin && <span className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] uppercase tracking-wider">Admin</span>}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        {m.disabled_at ? (
                                            <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Désactivé</span>
                                        ) : (
                                            <span className="text-xs text-secondary bg-secondary-light px-2 py-0.5 rounded-full">Actif</span>
                                        )}
                                    </td>
                                    <td className="px-5 py-4 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => setEditing(m)} className="p-2 hover:bg-primary/10 rounded text-foreground/65 hover:text-primary" title="Modifier" data-testid={`team-edit-${m.user_id}`}>
                                                <Pencil size={14} />
                                            </button>
                                            <button onClick={() => toggleDisabled(m)} className="p-2 hover:bg-amber-100 rounded text-foreground/65 hover:text-amber-700" title={m.disabled_at ? 'Réactiver' : 'Désactiver'} data-testid={`team-toggle-${m.user_id}`}>
                                                {m.disabled_at ? <UserCheck size={14} /> : <UserX size={14} />}
                                            </button>
                                            <button onClick={() => setConfirmDelete(m)} className="p-2 hover:bg-destructive/10 rounded text-foreground/65 hover:text-destructive" title="Supprimer" data-testid={`team-delete-${m.user_id}`}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Invite Dialog */}
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Inviter un utilisateur</DialogTitle></DialogHeader>
                    <form onSubmit={submitInvite} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Prénom</Label><Input name="first_name" required data-testid="invite-first" /></div>
                            <div><Label>Nom</Label><Input name="last_name" required data-testid="invite-last" /></div>
                        </div>
                        <div><Label>Email</Label><Input name="email" type="email" required data-testid="invite-email" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Téléphone</Label><Input name="phone" type="tel" placeholder="+33..." data-testid="invite-phone" /></div>
                            <div><Label>Fonction</Label><Input name="job_function" placeholder="ex: Directrice Programmes" data-testid="invite-function" /></div>
                        </div>
                        <div>
                            <Label>Rôle</Label>
                            <select name="role" defaultValue="pilote_projet" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="invite-role">
                                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" name="is_admin" data-testid="invite-is-admin" />
                            <span>Administrateur du workspace</span>
                        </label>
                        <div>
                            <Label>Mot de passe temporaire <span className="text-foreground/40 font-normal">(optionnel — laissez vide pour générer automatiquement)</span></Label>
                            <div className="flex gap-2">
                                <Input name="temp_password" minLength={10} placeholder="Min. 10 caractères"
                                       autoComplete="new-password" data-testid="invite-temp-password" />
                                <Button type="button" variant="outline" size="sm" className="h-10 shrink-0"
                                        data-testid="invite-generate-password"
                                        onClick={(e) => {
                                            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
                                            const arr = new Uint32Array(14);
                                            window.crypto.getRandomValues(arr);
                                            const p = Array.from(arr, x => chars[x % chars.length]).join('');
                                            e.currentTarget.closest('form').temp_password.value = p;
                                        }}>
                                    Générer
                                </Button>
                            </div>
                            <p className="text-[11px] text-foreground/50 mt-1">
                                L'utilisateur pourra le changer lui-même depuis son profil une fois connecté.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Annuler</Button>
                            <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="invite-submit">
                                <UserPlus size={14} className="mr-2" /> Envoyer l'invitation
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Modifier — {editing?.email}</DialogTitle></DialogHeader>
                    {editing && (
                        <form onSubmit={submitEdit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Prénom</Label><Input name="first_name" defaultValue={(editing.full_name || '').split(' ')[0] || ''} data-testid="edit-first" /></div>
                                <div><Label>Nom</Label><Input name="last_name" defaultValue={(editing.full_name || '').split(' ').slice(1).join(' ')} data-testid="edit-last" /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Téléphone</Label><Input name="phone" type="tel" defaultValue={editing.phone || ''} /></div>
                                <div><Label>Fonction</Label><Input name="job_function" defaultValue={editing.job_function || ''} /></div>
                            </div>
                            <div>
                                <Label>Rôle</Label>
                                <select name="role" defaultValue={editing.role} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" name="is_admin" defaultChecked={editing.is_admin} />
                                <span>Administrateur du workspace</span>
                            </label>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
                                <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="edit-submit">Enregistrer</Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Supprimer cet utilisateur du workspace ?</DialogTitle></DialogHeader>
                    {confirmDelete && (
                        <div className="space-y-4">
                            <p className="text-foreground/75 leading-[1.7]">
                                <strong>{confirmDelete.full_name || confirmDelete.email}</strong> sera retiré·e de cet espace.
                                Le compte utilisateur n'est pas supprimé — il peut être réinvité plus tard.
                            </p>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
                                <Button onClick={() => deleteMember(confirmDelete)} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground" data-testid="confirm-delete">
                                    <Trash2 size={14} className="mr-2" /> Confirmer la suppression
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Temp password reveal */}
            <Dialog open={!!tempPassword} onOpenChange={(o) => !o && setTempPassword(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{tempPassword?.applied === false ? 'Utilisateur existant ajouté' : 'Mot de passe temporaire'}</DialogTitle></DialogHeader>
                    {tempPassword && (
                        <div className="space-y-4">
                            {tempPassword.applied === false ? (
                                <p className="text-foreground/75 leading-[1.7]" data-testid="temp-password-existing">
                                    <strong>{tempPassword.email}</strong> possédait déjà un compte sur la plateforme :
                                    il a été ajouté à votre espace <strong>sans modifier son mot de passe actuel</strong> (sécurité).
                                    Il peut se connecter avec ses identifiants habituels.
                                </p>
                            ) : (
                                <>
                                    <p className="text-foreground/75 leading-[1.7]">
                                        Communiquez ce mot de passe temporaire à <strong>{tempPassword.email}</strong>.
                                        Une fois connecté, il pourra le changer lui-même depuis <strong>Mon profil → Sécurité</strong>.
                                    </p>
                                    <div className="font-mono text-lg bg-surface-muted p-4 rounded-md text-center select-all" data-testid="temp-password-value">
                                        {tempPassword.password}
                                    </div>
                                    <p className="text-xs text-foreground/60 leading-[1.7]">
                                        <Mail size={11} className="inline mr-1" /> Un email d'invitation a également été envoyé (si SMTP configuré).
                                    </p>
                                </>
                            )}
                            <DialogFooter>
                                <Button onClick={() => setTempPassword(null)} className="bg-primary hover:bg-primary-hover">Fermer</Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
